import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, UploadIcon, StarIcon, MoreHorizontalIcon } from 'lucide-react';
interface RoomImage {
  id: string;
  url: string;
  caption: string;
  uploadedBy: string;
  uploadDate: string;
  isActive: boolean;
  isFeatured?: boolean;
}
interface RoomAssetsProps {
  roomId: string;
  roomName: string;
  images: RoomImage[];
  onBack: () => void;
  onUpload: (files: FileList) => void;
  onUpdateImage: (imageId: string, updates: Partial<RoomImage>) => void;
  onDeleteImage: (imageId: string) => void;
}
export function RoomAssets({
  roomId,
  roomName,
  images,
  onBack,
  onUpload,
  onUpdateImage,
  onDeleteImage
}: RoomAssetsProps) {
  const {
    t
  } = useTranslation('rooms');
  const [activeTab, setActiveTab] = useState<'info' | 'media'>('media');
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onUpload(e.dataTransfer.files);
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onUpload(e.target.files);
    }
  };
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedImages(images.map(img => img.id));
    } else {
      setSelectedImages([]);
    }
  };
  const handleSelectImage = (imageId: string, checked: boolean) => {
    if (checked) {
      setSelectedImages([...selectedImages, imageId]);
    } else {
      setSelectedImages(selectedImages.filter(id => id !== imageId));
    }
  };
  return <div className="space-y-6" data-id="element-9731">
      <div className="flex items-center" data-id="element-9732">
        <button onClick={onBack} className="flex items-center text-gray-600 hover:text-gray-900" data-id="element-9733">
          <ArrowLeftIcon size={18} className="mr-1" data-id="element-9734" />
          <span data-id="element-9735">{t('components.RoomAssets.room_assets')}</span>
        </button>
      </div>
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-9736">
        {/* Tabs */}
        <div className="border-b border-gray-200" data-id="element-9737">
          <nav className="flex" role="tablist" aria-label="Room form sections" data-id="element-9738">
            <button role="tab" aria-selected={activeTab === 'info'} className={`px-6 py-4 text-sm font-medium ${activeTab === 'info' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('info')} data-id="element-9739">
              {t('components.EditRoomForm.room_information')}
            </button>
            <button role="tab" aria-selected={activeTab === 'media'} className={`px-6 py-4 text-sm font-medium ${activeTab === 'media' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('media')} data-id="element-9740">
              {t('components.EditRoomForm.media_assets')}
            </button>
          </nav>
        </div>
        {/* Media Assets Content */}
        <div className="p-6" data-id="element-9741">
          {/* File Upload Area */}
          <div className={`border-2 border-dashed rounded-lg p-8 mb-6 flex flex-col items-center justify-center ${dragActive ? 'border-red-500 bg-red-50' : 'border-gray-300'}`} onDragEnter={handleDrag} onDragOver={handleDrag} onDragLeave={handleDrag} onDrop={handleDrop} data-id="element-9742">
            <div className="p-3 bg-red-100 rounded-full mb-4" data-id="element-9743">
              <UploadIcon size={24} className="text-red-500" data-id="element-9744" />
            </div>
            <p className="text-gray-600 mb-1" data-id="element-9745">
              {t('components.RoomAssets.drag_files_here')}
            </p>
            <p className="text-xs text-gray-500 mb-4" data-id="element-9746">
              {t('components.RoomAssets.supported_formats_png_jpg_jpeg')}
            </p>
            <p className="text-xs text-gray-500 mb-4" data-id="element-9747">
              {t('components.RoomAssets.max_file_size_10mb')}
            </p>
            <label className="cursor-pointer" data-id="element-9748">
              <input type="file" accept="image/png,image/jpeg,image/jpg" className="hidden" onChange={handleFileChange} multiple data-id="element-9749" />

              <span className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700" data-id="element-9750">
                {t('components.RoomAssets.upload')}
              </span>
            </label>
          </div>
          {/* Images Table */}
          <div className="overflow-x-auto" data-id="element-9751">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-9752">
              <thead data-id="element-9753">
                <tr data-id="element-9754">
                  <th className="w-12 py-3 pl-4" data-id="element-9755">
                    <input type="checkbox" checked={selectedImages.length === images.length && images.length > 0} onChange={handleSelectAll} className="h-4 w-4 text-red-600 border-gray-300 rounded" data-id="element-9756" />
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9757">
                    {t('components.RoomAssets.room_image')}
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9758">
                    {t('components.RoomAssets.caption')}
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9759">
                    {t('components.RoomAssets.uploaded_by')}
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9760">
                    {t('components.RoomAssets.uploaded')}
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9761">
                    {t('components.RoomAssets.visibility')}
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9762">
                    {t('components.RoomAssets.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-9763">
                {images.map(image => <tr key={image.id} data-id="element-9764">
                    <td className="py-4 pl-4" data-id="element-9765">
                      <input type="checkbox" checked={selectedImages.includes(image.id)} onChange={e => handleSelectImage(image.id, e.target.checked)} className="h-4 w-4 text-red-600 border-gray-300 rounded" data-id="element-9766" />
                    </td>
                    <td className="py-4" data-id="element-9767">
                      <div className="w-24 h-16 bg-gray-100 rounded overflow-hidden" data-id="element-9768">
                        <img src={image.url} alt={image.caption || 'Room image'} className="w-full h-full object-cover" data-id="element-9769" />
                      </div>
                    </td>
                    <td className="py-4" data-id="element-9770">
                      <div className="flex items-center" data-id="element-9771">
                        {image.isFeatured && <StarIcon size={16} className="text-yellow-400 mr-1" data-id="element-9772" />}
                        <span data-id="element-9773">{image.caption || 'Untitled'}</span>
                      </div>
                    </td>
                    <td className="py-4 text-sm text-gray-500" data-id="element-9774">
                      {image.uploadedBy}
                    </td>
                    <td className="py-4 text-sm text-gray-500" data-id="element-9775">
                      {image.uploadDate}
                    </td>
                    <td className="py-4" data-id="element-9776">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${image.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-9777">
                        {image.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-4" data-id="element-9778">
                      <button aria-label="Image actions" className="text-gray-400 hover:text-gray-500" data-id="element-9779">
                        <MoreHorizontalIcon size={16} aria-hidden="true" data-id="element-9780" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>;
}